/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React, { useState, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { TextField, Grid, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material'

import { scenarioStatus, scenarioPhases, variantTypes } from '../../../../constants'
import { FormTitle } from '../../../../components'
import { Logger, LoggerTypes } from '../../../../utils'
import './style.scss'

const defaultValues = {
  name: '',
  description: '',
  status: 'DRAFT', // <-- scenarioStatus.DRAFT
  phase: 'ASSEMBLY' // <-- scenarioPhases.ASSEMBLY
}

export const ScenarioForm = forwardRef(({ setOpenDialog, title, scenario, dispatchAction }, ref) => {
  const { t } = useTranslation()

  const [name, setName] = useState(scenario.name || defaultValues.name)
  const [description, setDescription] = useState(scenario.description || defaultValues.description)
  const [status, setStatus] = useState(scenario.status || defaultValues.status)
  const [phase, setPhase] = useState(scenario.phase || defaultValues.phase)
  const [errors, setErrors] = useState(null)

  const onChangeName = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioForm.onChangeName()', { name: _value })
    setName(_value)
  }

  const onChangeDescription = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioForm.onChangeDescription()', { description: _value })
    setDescription(_value)
  }

  const onChangeStatus = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioForm.onChangeStatus()', { status: _value })
    setStatus(_value)
  }

  const onChangePhase = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioForm.onChangePhase()', { phase: _value })
    setPhase(_value)
  }

  // ----

  const onCloseDialog = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioForm.onCloseDialog()')
    setOpenDialog(false)
  }

  useImperativeHandle(ref, () => ({
    async onSubmitForm() {
      const newScenario = {
        id: scenario?.id || null,
        name: name === '' ? null : name,
        description: description === '' ? null : description,
        status: status === '' ? null : status,
        phase: phase === '' ? null : phase
      }

      dispatchAction(
        newScenario,
        () => {
          Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioForm.onSubmitForm()._onSuccess()', { newScenario })

          onCloseDialog()
        },
        (_errors) => {
          Logger.trace(LoggerTypes.WARN, 'ScenarioForm.onSubmitForm()._onFail()', { _errors })

          setErrors(_errors)
        }
      )
    }
  }))

  Logger.trace(LoggerTypes.CONTAINER, 'ScenarioForm.render()', {
    scenario,
    values: { name, description, status, phase }
  })

  return (
    <div className="ScenarioForm">
      <FormTitle title={title} />
      <form>
        <Grid container spacing={2} className="formContainer">
          {/*  name  */}
          <Grid item xs={12} sm={12}>
            <TextField
              type="text"
              name="name"
              id="name"
              className="TextField"
              value={name}
              label={t('scenarios.field.name.label')}
              onChange={onChangeName}
              placeholder={t('scenarios.field.name.placeholder')}
              error={Boolean(errors && errors?.name)}
              helperText={(errors && errors?.name) || ''}
            />
          </Grid>

          {/*  description  */}
          <Grid item xs={12} sm={12}>
            <TextField
              type="text"
              name="description"
              id="description"
              className="TextField"
              value={description}
              multiline={true}
              rows={4}
              label={t('scenarios.field.description.label')}
              onChange={onChangeDescription}
              placeholder={t('scenarios.field.description.placeholder')}
              error={Boolean(errors && errors?.description)}
              helperText={(errors && errors?.description) || ''}
            />
          </Grid>

          {/*  status  */}
          <Grid item xs={12} sm={12}>
            <FormControl error={Boolean(errors && errors?.status)} className="common_fullwidth">
              <InputLabel id="status-label" style={{ backgroundColor: 'white', padding: '0 5px' }}>
                {t('scenarios.field.status.label')}
              </InputLabel>
              <Select
                labelId="status-label"
                id="status"
                className="common_fullwidth"
                value={status}
                variant={variantTypes.OUTLINED}
                onChange={onChangeStatus}
              >
                {Object.values(scenarioStatus).map((_status, index) => (
                  <MenuItem key={index} value={_status}>
                    <>
                      <span
                        className="bulletPoint"
                        style={{
                          background:
                            _status === scenarioStatus.OFFICIAL
                              ? 'green'
                              : _status === scenarioStatus.ARCHIVED
                              ? 'red'
                              : 'orange'
                        }}
                      />
                      <span className="">{t(`scenarios.values.status.${String(_status).toLocaleLowerCase()}`)}</span>
                    </>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.status) || ''}</FormHelperText>
            </FormControl>
          </Grid>

          {/*  phase  */}
          <Grid item xs={12} sm={12}>
            <FormControl error={Boolean(errors && errors?.phase)} className="common_fullwidth">
              <InputLabel id="phase-label" style={{ backgroundColor: 'white', padding: '0 5px' }}>
                {t('scenarios.field.phase.label')}
              </InputLabel>
              <Select
                labelId="phase-label"
                id="phase"
                className="common_fullwidth"
                value={phase}
                variant={variantTypes.OUTLINED}
                onChange={onChangePhase}
              >
                {Object.values(scenarioPhases).map((_phase, index) => (
                  <MenuItem key={index} value={_phase}>
                    {t(`scenarios.values.phases.${String(_phase).toLocaleLowerCase()}`)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.phase) || ''}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </form>
    </div>
  )
})

ScenarioForm.displayName = 'ScenarioForm'

ScenarioForm.propTypes = {
  setOpenDialog: PropTypes.func.isRequired,
  dispatchAction: PropTypes.func.isRequired,
  title: PropTypes.string,
  scenario: PropTypes.object.isRequired
}

ScenarioForm.defaultProps = {
  setOpenDialog: () => {},
  dispatchAction: () => {},
  title: null,
  scenario: defaultValues
}
